// Figma: https://www.figma.com/design/nykbXADUTliE6d7xv6FjcQ/NEW-TSAB-Shared-Customer-Library?node-id=199-4806
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { Marked } from 'marked'
import { getDepartureNotificationItems } from '~/utils/data'
import { showInfoModal } from '~/lib/modal'

// Dedicated Marked instance with notification modal styling from Figma
// node: NEW-TSAB-Shared-Customer-Library / 4480-3533
const notifMarked = new Marked({
  breaks: true,
  renderer: {
    heading({ tokens, depth }) {
      const content = this.parser.parseInline(tokens)
      if (depth === 1) {
        return `<p style="font-size:16px;font-weight:600;text-transform:uppercase;font-stretch:condensed;line-height:normal;color:#17181a;padding-bottom:8px;">${content}</p>`
      }
      return `<p style="font-size:16px;font-weight:600;font-stretch:condensed;line-height:24px;letter-spacing:0.32px;text-transform:uppercase;color:#17181a;padding-top:12px;padding-bottom:4px;">${content}</p>`
    },
    paragraph({ tokens }) {
      const content = this.parser.parseInline(tokens)
      return `<p style="font-size:15px;font-weight:400;line-height:20px;color:#5d5d5f;padding-bottom:16px;">${content}</p>`
    },
    link({ href, title, tokens }) {
      const text = this.parser.parseInline(tokens)
      const t = title ? ` title="${title}"` : ''
      return `<a href="${href}"${t} target="_blank" rel="noopener noreferrer">${text}</a>`
    },
    list({ ordered, items }) {
      if (!ordered) {
        const body = items.map(item => {
          const inlineTokens = item.tokens?.[0]?.tokens ?? []
          const content = this.parser.parseInline(inlineTokens)
          return `<li style="font-size:15px;line-height:20px;color:#5d5d5f;padding-bottom:10px;">${content}</li>`
        }).join('')
        return `<ul style="padding-left:15px;margin:0;padding-bottom:8px;list-style-type:disc;">${body}</ul>`
      }
      return items.map((item, i) => {
        const inlineTokens = item.tokens?.[0]?.tokens ?? []
        const content = this.parser.parseInline(inlineTokens)
        return `<div style="display:flex;gap:12px;align-items:flex-start;padding:8px 0;">` +
          `<div style="background:#17181a;color:white;border-radius:24px;width:22px;height:22px;font-size:15px;font-weight:500;text-align:center;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;">${i + 1}</div>` +
          `<div style="font-size:15px;font-weight:400;line-height:20px;color:#17181a;flex:1;">${content}</div>` +
          `</div>`
      }).join('')
    },
  },
})

// Shared with the booking card (components/Booking), so location info reads the same there.
export const parseNotificationMarkdown = text => notifMarked.parse(text || '')
export const stripMarkdown = text => (text || '').split('\n').find(l => l.trim()) ?.replace(/[#*_`~[\]()>]/g, '').trim() ?? ''

// Modal with the location's name as heading followed by its info texts, rendered as in the
// booking flow's "Important information about your trip".
export const showLocationInfoModal = (title, messages) => {
  let section = ''
  if (title) section += `<p style="font-size:26px;font-weight:600;line-height:24px;letter-spacing:0.32px;color:#17181a;padding-bottom:16px;">${title}</p>`
  section += (messages || []).map(parseNotificationMarkdown).join('')
  showInfoModal(null, section, [{ title: COPY.booking.button_ok }])
}

const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
    <g clipPath="url(#notif-clip)">
      <path fillRule="evenodd" clipRule="evenodd" d="M8 14.4333C11.553 14.4333 14.4333 11.553 14.4333 8C14.4333 4.44697 11.553 1.56667 8 1.56667C4.44697 1.56667 1.56667 4.44697 1.56667 8C1.56667 11.553 4.44697 14.4333 8 14.4333ZM8 15.5C12.1421 15.5 15.5 12.1421 15.5 8C15.5 3.85786 12.1421 0.5 8 0.5C3.85786 0.5 0.5 3.85786 0.5 8C0.5 12.1421 3.85786 15.5 8 15.5Z" fill="#17181A"/>
      <rect x="7.2" y="4" width="1.6" height="1.6" rx="0.8" fill="#17181A"/>
      <rect x="7.467" y="6.667" width="1.067" height="5.333" rx="0.213" fill="#17181A"/>
    </g>
    <defs>
      <clipPath id="notif-clip"><rect width="16" height="16" fill="white"/></clipPath>
    </defs>
  </svg>
)

const ChevronRightIcon = () => (
  <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
    <path d="M1 1L7 7L1 13" stroke="#17181A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

export default function Notifications({ fullscreen, forceMessages, showBothBookingTypes = false }) {
  const { t } = useTranslation()
  const { values } = useFormikContext()

  if (forceMessages) {
    if (forceMessages.length === 0) return null
    return (
      <div style={{ overflow: 'hidden' }}>
        {forceMessages.map((item, i) => (
          <button key={i} type="button" className="w-full flex items-center gap-2 px-4 h-14 rounded-[10px] bg-secondary-container border-[1.5px] border-solid border-secondary text-left">
            <InfoIcon />
            <span className="flex-1 text-body-sm font-medium text-on-surface" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item?.shortMessage || item}
            </span>
          </button>
        ))}
      </div>
    )
  }

  // Departure notifications: new tag logic applied here so filtering is always
  // reactive to current pickupTime without needing to rebuild originInfo.
  const depItems = getDepartureNotificationItems(
    values.departure?.locationInformation,
    values.pickupTime,
    showBothBookingTypes
  )
  const depMessages = depItems.map(item => item.message).filter(Boolean)

  // Destination notifications: INFO tag items stored in originInfo
  const destInfo = values.originInfo?.filter(item => item.info?.length > 0) || []
  const destMessages = destInfo.flatMap(item => item.info)

  const totalCount = depMessages.length + destMessages.length
  if (totalCount === 0) return null

  const shortMessage = stripMarkdown(depMessages[0] || destMessages[0])
  const multipleInfo = totalCount > 1

  const SECTION_GAP = '<div style="height:24px;"></div>'

  const readMore = () => {
    const sections = []
    let title = "";
    if (depMessages.length > 0) {
      title = values.departure?.name || values.departure?.address?.poiName
      let section = ''
      if (title) section += `<p style="font-size:26px;font-weight:600;line-height:24px;letter-spacing:0.32px;color:#17181a;padding-bottom:16px;">${title}</p>`
      section += depMessages.map(parseNotificationMarkdown).join('')
      sections.push(section)
    }
    destInfo.forEach(item => {
      let section = ''
      if (item.title) title = item.title; //section += `<p style="font-size:16px;font-weight:600;line-height:24px;letter-spacing:0.32px;text-transform:uppercase;color:#17181a;padding-bottom:4px;">${item.title}</p>`
      section += item.info.map(parseNotificationMarkdown).join('')
      sections.push(section)
    })
    showInfoModal(null, sections.join(SECTION_GAP), [{ title: COPY.booking.button_ok }])
  }

  return (
    <div className={fullscreen ? 'px-0' : ''} style={{ overflow: 'hidden' }}>
      <button
        type="button"
        onClick={readMore}
        className="w-full flex items-center gap-2 px-4 h-14 rounded-[10px] bg-secondary-container border-[1.5px] border-solid border-secondary text-left"
      >
        <InfoIcon />
        <span className="flex-1 text-body-sm font-medium text-on-surface truncate" style={{ minWidth: 0 }}>
          {multipleInfo ? t('label-important-about-your-trip') : shortMessage}
        </span>
        <ChevronRightIcon />
      </button>
    </div>
  )
}
